import { useCallback, useMemo, useState } from 'react';
import {
  useGetMemberEntriesCoordsQuery,
  useGetMemberSignInCoordsQuery
} from '@bo-services/members/membersApi';
import type { MapPoints } from '@library/types/maps';
import type { MemberCoordsRequest } from '@library/types/memberProfileMap';
import type { MemberProfileMapContextType } from '@bo-hooks/members/useMemberProfileMapContext';
import { formatDateToString } from '@library/component-utils/dateTime';

export const useMemberProfileMap = ({ memberId }: { memberId: string }) => {
  const [entriesChecked, setEntriesChecked] = useState(true);
  const [showMapTable, setShowMapTable] = useState(true);
  const [createdAtDate, setCreatedAtDate] = useState<Date | undefined>(
    undefined
  );

  const memberCoordsRequest: MemberCoordsRequest = useMemo(() => {
    const formatDate = formatDateToString(createdAtDate);

    const request = {
      id: memberId,
      ...(createdAtDate && { created_at_after: `${formatDate} 00:00:00` }),
      ...(createdAtDate && { created_at_before: `${formatDate} 23:59:59` })
    };

    return request;
  }, [createdAtDate, memberId]);

  const {
    data: signInCoords,
    isError: isFetchSignInCorrdsError,
    isFetching: isFetchingSignInCorrds
  } = useGetMemberSignInCoordsQuery(memberCoordsRequest, {
    skip: !memberId || entriesChecked
  });

  const {
    data: entryCoords,
    isError: isFetchEntriesCorrdsError,
    isFetching: isFetchingEntriesCorrds
  } = useGetMemberEntriesCoordsQuery(memberCoordsRequest, {
    skip: !memberId || !entriesChecked
  });

  const mapPoints: MapPoints = useMemo(() => {
    const mapData = entriesChecked ? entryCoords : signInCoords;

    return (
      mapData?.map((coords) => {
        return {
          markers: coords.map((mapPoint) => {
            const { createdAtDisplay, id } = mapPoint;
            return { createdAtDisplay, id };
          }),
          position: coords[0].position
        };
      }) ?? []
    );
  }, [entriesChecked, entryCoords, signInCoords]);

  const handleEntriesChecked = useCallback(() => {
    setCreatedAtDate(undefined);
    setEntriesChecked((prevValue) => !prevValue);
  }, []);

  const handleShowMapTable = useCallback(
    () => setShowMapTable((prevValue) => !prevValue),
    []
  );

  const handleFilterChange = useCallback((value: Date | null) => {
    if (!value) {
      setCreatedAtDate(undefined);
      return;
    }

    setCreatedAtDate(value);
  }, []);

  const isError = isFetchEntriesCorrdsError || isFetchSignInCorrdsError;
  const isLoading = isFetchingEntriesCorrds || isFetchingSignInCorrds;

  const mapContext: MemberProfileMapContextType = {
    createdAtDate,
    data: entriesChecked ? entryCoords : signInCoords,
    entriesChecked,
    isError,
    isLoading,
    mapPoints,
    markerType: entriesChecked ? 'ENTRIES' : 'SIGN_INS',
    onEntriesChecked: handleEntriesChecked,
    onFilterChange: handleFilterChange,
    onShowMapTable: handleShowMapTable,
    showMapTable
  };

  return mapContext;
};
